import { BigNum, QUOTE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import Env from 'src/environmentVariables/EnvironmentVariables';
import captureException from 'src/utils/captureException';
import LoggingService from 'src/utils/LoggingService';
import { encodeQueryParams } from 'src/utils/explorer';

export type UnsettledPnlUser = {
	userPubKey: PublicKey;
	pnl: BigNum;
};

export type UnsettledPnlUsersData = {
	marketIndex: number;
	gainers: UnsettledPnlUser[];
	losers: UnsettledPnlUser[];
};

type RawPnlUser = { userPubKey: string; pnl: number };

const toUnsettledPnlUser = (user: RawPnlUser): UnsettledPnlUser => ({
	userPubKey: new PublicKey(user.userPubKey),
	pnl: BigNum.fromPrint(user.pnl.toString(), QUOTE_PRECISION_EXP),
});

async function fetchUnsettledPnlUsers(
	marketIndex: number
): Promise<UnsettledPnlUsersData> {
	const queryParams = encodeQueryParams({ marketIndex });
	const url = `${Env.dlobServerHttpUrl}/unsettledPnlUsers?${queryParams}`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new Error(`Failed to fetch unsettled PnL users: ${response.status}`);
	}

	const resp = await response.json();

	return {
		marketIndex: resp.marketIndex,
		gainers:
			resp.gainers
				?.filter((g: RawPnlUser) => g.pnl > 0)
				.map(toUnsettledPnlUser) ?? [],
		losers:
			resp.losers
				?.filter((l: RawPnlUser) => l.pnl < 0)
				.map(toUnsettledPnlUser) ?? [],
	};
}

export const useUnsettledPnlUsersQuery = (marketIndex: number) => {
	return useQuery({
		queryKey: ['unsettled_pnl_users', marketIndex] as const,
		queryFn: async () => {
			try {
				return await fetchUnsettledPnlUsers(marketIndex);
			} catch (e: any) {
				captureException(e);
				LoggingService.error(
					`Caught error fetching unsettled pnl users from dlob server`
				);
				throw e;
			}
		},
		staleTime: 1000 * 30, // 30 seconds
		gcTime: 1000 * 60 * 2, // 2 minutes
	});
};
